import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useMyReports, type UserReport } from '../hooks/useMyReports';
import BottomNavigation from '@/components/ui/BottomNavigation';
import { CheckCircle2, Clock, Eye, User as UserIcon, MessageSquareText, MessageCircleWarning } from 'lucide-react';
import { toPersianNumber } from '@/utils/persianNumber';

// Persian (Jalali) date like: 29 شهریور 1404
const formatPersianDate = (dateString?: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  try {
    const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian-nu-latn', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    }).formatToParts(date);
    const day = parts.find(p => p.type === 'day')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const year = parts.find(p => p.type === 'year')?.value || '';
    if (day && month && year) return toPersianNumber(`${Number(day)} ${month} ${year}`);
    return toPersianNumber(parts.map(p => p.value).join(' '));
  } catch {
    return '';
  }
};

import type { LucideIcon } from 'lucide-react';

const statusMeta: Record<UserReport['status'], { label: string; color: string; Icon: LucideIcon }> = {
  pending: { label: 'در حال بررسی', color: 'bg-amber-500', Icon: Clock },
  resolved: { label: 'حل شده', color: 'bg-emerald-600', Icon: CheckCircle2 },
  reviewed: { label: 'بررسی شده', color: 'bg-blue-600', Icon: Eye },
  ignored: { label: 'نادیده شده', color: 'bg-gray-400', Icon: Eye },
};

const targetMeta: Record<'user' | 'message' | 'comment', { label: string; color: string; Icon: LucideIcon }> = {
  user: { label: 'کاربر', color: 'text-violet-600', Icon: UserIcon },
  message: { label: 'پیام', color: 'text-sky-600', Icon: MessageSquareText },
  comment: { label: 'کامنت', color: 'text-teal-600', Icon: MessageCircleWarning },
};

const ReportHistory = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useMyReports();
  const items = data?.items ?? [];

  const handleGoBack = () => navigate(-1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-20" dir="rtl">
      {/* Header */}
      <div className="bg-[#ffffff] px-4 py-3 flex items-center justify-between sticky top-0 z-50 pt-safe-top">
        <div className="flex items-center justify-between w-full mt-2">
        <button
          onClick={handleGoBack}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
        <h1 className="text-sm md:text-base font-bold text-foreground">تاریخچه گزارش‌ها</h1>
        <div className="w-8"></div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="container mx-auto p-4 pt-6 pb-6">

        {/* Loading / Error / Empty */}
        {isLoading ? (
          <div className="min-h-[40vh] flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-xs md:text-sm text-muted-foreground font-persian">در حال بارگذاری...</p>
            </div>
          </div>
        ) : error ? (
          <div className="min-h-[40vh] flex items-center justify-center">
            <div className="text-center text-xs md:text-sm text-red-600">خطا در بارگذاری تاریخچه گزارش‌ها</div>
          </div>
        ) : items.length === 0 ? (
          <div className="min-h-[40vh] flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-3">🗒️</div>
              <p className="text-sm md:text-base font-bold text-foreground">شما تا الان گزارشی ثبت نکردی</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {items.map((r: UserReport) => {
                const t = targetMeta[r.target_type];
                const s = statusMeta[r.status] || statusMeta.pending;
                const TIcon = t?.Icon;
                const SIcon = s.Icon;
                return (
                  <li key={r.report_id} className="p-4">
                    {/* Top row: meta + status */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {TIcon ? <TIcon className={`w-5 h-5 ${t.color}`} /> : null}
                        <span className="text-xs md:text-sm px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{t?.label || r.target_type}</span>
                        <span className="text-[10px] md:text-xs text-muted-foreground">{formatPersianDate(r.created_at)}</span>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <span className={`inline-flex items-center gap-2 text-xs md:text-sm font-persian`}>
                          <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                          <SIcon className="w-4 h-4 text-gray-600" />
                          <span>{s.label}</span>
                        </span>
                      </div>
                    </div>
                    {/* Reason full width */}
                    <p className="mt-2 text-xs md:text-sm text-foreground font-persian whitespace-pre-wrap break-words leading-relaxed">
                      {toPersianNumber(r.reason)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </motion.div>
      <BottomNavigation />
    </div>
  );
};

export default ReportHistory;
